<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('dailyReport.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('dailyReport.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <div class="row">
      <div class="col-md-3">
        <ts-card>
          <ts-card-wrapper>
            <ts-loading-banner :loading="loading">
              <form>
                <fieldset>
                  <legend>{{ $t('dailyReport.criteria') }}</legend>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label class="required">{{
                        $t('dailyReport.date')
                      }}</label>
                      <a-range-picker
                        style="width:100%"
                        v-model="model.date_range"
                        valueFormat="DD-MM-YYYY HH:mm"
                        :show-time="{ format: 'HH:mm' }"
                        format="DD-MM-YYYY HH:mm"
                      />
                      <div class="validate" v-if="errors.has('date_range')">
                        {{ errors.first('date_range') }}
                      </div>
                    </div>
                  </div>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label class="required">{{
                        $t('dailyReport.branch')
                      }}</label>
                      <a-select
                        style="width: 100%"
                        placeholder="All"
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        v-model="model.branch_id"
                        :options="branchList"
                        @change="getLocations"
                      ></a-select>
                      <div class="validate" v-if="errors.has('branch_id')">
                        {{ errors.first('branch_id') }}
                      </div>
                    </div>
                  </div>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label class="form-label col-label">{{
                        $t('dailyReport.warehouse')
                      }}</label>
                      <a-select
                        v-model="model.location_id"
                        :defaultActiveFirstOption="true"
                        showSearch
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        :options="locations"
                        :placeholder="$t('all')"
                        class="ts-w-full"
                      ></a-select>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-12">
                      <label>{{ $t('stockTransferReport.reportType') }}</label>
                      <div class="form-group">
                        <div class="radio radio-css radio-inline">
                          <input
                            v-model="model.report_type"
                            type="radio"
                            id="cashier"
                            :value="'cashier'"
                          />
                          <label for="cashier"> By cashier </label>
                        </div>
                        <div class="radio radio-css radio-inline">
                          <input
                            v-model="model.report_type"
                            type="radio"
                            id="sale"
                            :value="'sale'"
                          />
                          <label for="sale"> By sale </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row form-group"
                    v-if="model.report_type == 'cashier'"
                  >
                    <div class="col-md-12">
                      <label class="required">{{
                        $t('dailyReport.cashier')
                      }}</label>
                      <a-select
                        style="width: 100%"
                        mode="multiple"
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        v-model="model.cashier_id"
                        :options="cashiers"
                        placeholder="All"
                      ></a-select>
                      <div class="validate" v-if="errors.has('cashier_id')">
                        {{ errors.first('cashier_id') }}
                      </div>
                    </div>
                  </div>
                  <div
                    class="row form-group"
                    v-if="model.report_type == 'sale'"
                  >
                    <div class="col-md-12">
                      <label class="required">{{
                        $t('dailyReport.saleName')
                      }}</label>
                      <a-select
                        style="width: 100%"
                        mode="multiple"
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        v-model="model.sale_id"
                        :options="sales"
                        placeholder="All"
                      ></a-select>
                      <div class="validate" v-if="errors.has('sale_id')">
                        {{ errors.first('sale_id') }}
                      </div>
                    </div>
                  </div>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label>{{ $t('dailyReport.status') }}</label>
                      <a-select
                        style="width: 100%"
                        v-model="model.status"
                        mode="multiple"
                        :options="paymentStatus"
                        placeholder="All"
                      >
                      </a-select>
                      <div class="validate" v-if="errors.has('status')">
                        {{ errors.first('status') }}
                      </div>
                    </div>
                  </div>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label>{{ $t('dailyReport.type') }}</label>
                      <a-select
                        style="width: 100%"
                        v-model="model.type"
                        mode="multiple"
                        :options="posType"
                        placeholder="All"
                      >
                      </a-select>
                      <div class="validate" v-if="errors.has('type')">
                        {{ errors.first('type') }}
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="form-group">
                      <div class="col-md-12">
                        <a-radio-group v-model="model.print">
                          <a-radio value="a4">A4</a-radio>
                          <a-radio value="a5">A5</a-radio>
                          <a-radio value="receipt">Receipt</a-radio>
                        </a-radio-group>
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-6 text-right">
                      <ts-button
                        class="btn-block"
                        color="success"
                        @click.prevent="exportExcel"
                        :waiting="exporting"
                        ><i class="fas fa-file-excel"></i>
                        {{ $t('excel') }}</ts-button
                      >
                    </div>
                    <div class="col-md-6 text-right">
                      <ts-button
                        class="btn-block"
                        color="primary"
                        @click.prevent="preview"
                        :waiting="waiting"
                        ><i class="fas fa-file-pdf text-red"></i>
                        {{ $t('preview') }}</ts-button
                      >
                    </div>
                  </div>
                </fieldset>
              </form>
            </ts-loading-banner>
          </ts-card-wrapper>
        </ts-card>
      </div>
      <div class="col-md-9">
        <ts-card>
          <ts-card-wrapper>
            <form>
              <fieldset>
                <legend>{{ $t('dailyReport.preview') }}</legend>
                <ts-preview-pdf v-model="waiting" :src="src" />
              </fieldset>
            </form>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import moment from 'moment'
import { Errors } from 'form-backend-validation'
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'

export default {
  name: 'dailyIncome',
  data() {
    return {
      loading: false,
      waiting: false,
      exporting: false,
      src: null,
      errors: new Errors(),
      cashiers: [],
      branchList: [],
      locations: [],
      sales: [],
      printOptions: ['a4', 'a5', 'receipt'],
      model: {
        branch_id: undefined,
        location_id: undefined,
        cashier_id: undefined,
        sale_id: undefined,
        status: undefined,
        print: 'a4',
        report_type: 'cashier',
        type: undefined,
        date_range: [
          moment()
            .startOf('day')
            .format('DD-MM-YYYY HH:mm'),
          moment()
            .endOf('day')
            .format('DD-MM-YYYY HH:mm')
        ]
      }
    }
  },
  async created() {
    this.fetchData()
    this.model.location_id = this.$store.state.authUser.user.warehouses.location_id
  },
  computed: {
    paymentStatus() {
      return [
        { value: 'Paid', label: 'Paid' },
        { value: 'Partially paid', label: 'Partially Paid' },
        { value: 'Unpaid', label: 'Unpaid' }
      ]
    },
    posType() {
      return [
        { value: 'Retail sale', label: 'Retail sale' },
        { value: 'Whole sale', label: 'Whole sale' }
      ]
    }
  },
  methods: {
    ...mapActions('report/daily', ['store', 'user', 'sale']),
    async fetchData() {
      this.loading = true
      await this.$store
        .dispatch('security/user/getBranchAssigned')
        .then(response => {
          this.branchList = response.data.map(el => ({
            value: el.branch_id,
            label: el.branch_name_en
          }))
          this.model.branch_id = this.$store.state.authUser.user.default_branch.branch_id
          this.getLocations(false)
        })
      await this.sale().then(response => {
        this.sales = response.data.map(el => ({
          value: el.employee_id,
          label: el.employee_name_en
        }))
      })
      await this.user()
        .then(response => {
          this.cashiers = response.data.map(el => ({
            value: el.user_id,
            label: el.name
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    async getLocations($check) {
      this.loading = true
      if ($check) {
        this.model.location_id = undefined
      }
      this.locations = []
      await this.$store
        .dispatch('report/resource/getLocation', {
          branch_id: this.model.branch_id
        })
        .then(response => {
          this.locations = response.data.map(el => ({
            value: el.location_id,
            label: el.location_name_en
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    preview() {
      this.waiting = true
      this.src = ''
      this.errors = new Errors()
      this.store(this.model)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    exportExcel() {
      this.exporting = true
      this.errors = new Errors()
      this.$store
        .dispatch('report/daily/incomeExcel', this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    }
  },
  watch: {
    'model.report_type': function() {
      this.model.cashier_id = undefined
      this.model.sale_id = undefined
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('report/daily/RESET_STATE')
    this.$store.commit('report/daily/CLEAR_STATE')
    next()
  }
}
</script>
